import { isApiFailure } from './http';
import type { SessionDto } from './types';

/**
 * Состояние аккаунта приложения.
 *
 * ТЗ §5: «аккаунт создаётся автоматически при первом входе, формы регистрации нет».
 * Значит, строку `users` заводит первый же запуск — `POST /api/session`, — а не первая
 * запись: иначе клиент, впервые открывший Mini App, получал бы 404 на кнопку «Записаться».
 *
 * Состояние — размеченное объединение, и отказов в нём ДВА, а не один. Это главное решение
 * этого файла:
 *
 *   • `unsigned` — подписи нет вовсе. Вне Telegram `initData` не существует, и транспорт
 *     отказывает ключом `err.noInitData` ещё до отправки (см. `authHeader`). Это нормальное
 *     состояние открытого в браузере приложения, а не поломка: витрина работает, пугать
 *     красной плашкой не за что.
 *   • `failed` — подпись была, а сервер не завёл аккаунт. Вот это настоящий сбой: защищённые
 *     действия после него работать не будут, и молчать о нём нельзя (AGENTS.md, правило 7).
 *
 * Свести их в один статус нельзя ни в ту, ни в другую сторону: общий «ошибка» кричал бы
 * в браузере на ровном месте, а общий «ничего страшного» спрятал бы реальный отказ бэкенда.
 */
export type SessionState =
  /** Ещё не пробовали: приложение только что смонтировалось. */
  | { readonly status: 'idle' }
  | { readonly status: 'pending' }
  | { readonly status: 'ready'; readonly user: SessionDto }
  | { readonly status: 'unsigned'; readonly error: unknown }
  | { readonly status: 'failed'; readonly error: unknown };

/**
 * Отказ означает «подписывать нечем», а не «сервер отказал».
 *
 * Ключ ровно один: `err.noInitData` рождается в транспорте ДО сетевого запроса. Всё
 * остальное, включая `err.initDataInvalid` и `err.initDataExpired`, — это уже ответ
 * сервера на предъявленную подпись, то есть настоящая ошибка: приложение открыто
 * из Telegram, а войти не удалось.
 */
export function isMissingSignature(error: unknown): boolean {
  return isApiFailure(error) && error.key === 'err.noInitData';
}

export interface SessionController {
  getState(): SessionState;
  /** Подписка для React (`useSyncExternalStore`). Возвращает отписку. */
  subscribe(listener: () => void): () => void;
  /**
   * Гарантирует заведённый аккаунт.
   *
   * Она же — повторная попытка: после отказа следующий вызов идёт на сервер заново.
   * Отдельного `retry()` нет намеренно, потому что это одно и то же действие, а две
   * входные точки разошлись бы в условиях (кнопка повторяет, а гейт запроса — нет).
   */
  ensure(): Promise<SessionDto>;
}

/**
 * Контроллер сессии: один запрос на запуск приложения.
 *
 * Отдельный модуль без React намеренно. Во-первых, его логику (один запрос, а не по одному
 * на рендер; повтор после отказа; разделение двух видов отказа) можно проверить тестами
 * в окружении `node`, без jsdom. Во-вторых, к нему обращается не только экран, но и гейт
 * защищённых запросов в `api/client.ts`, а тот про React знать не должен.
 */
export function createSessionController(
  startSession: () => Promise<SessionDto>,
): SessionController {
  let state: SessionState = { status: 'idle' };
  // Запрос «в полёте». Именно он не даёт завести второго пользователя, когда сессию
  // одновременно просят монтирование приложения и нажатая кнопка «Записаться»:
  // оба ждут ОДНО обещание. Эндпоинт идемпотентен, но лишний round-trip на старте —
  // это лишняя секунда до первого экрана.
  let inFlight: Promise<SessionDto> | null = null;

  const listeners = new Set<() => void>();

  function publish(next: SessionState): void {
    state = next;
    for (const listener of listeners) listener();
  }

  function attempt(): Promise<SessionDto> {
    publish({ status: 'pending' });

    const running: Promise<SessionDto> = startSession().then(
      (user) => {
        // Проверка «это всё ещё мой запрос» на случай, если состояние успело смениться
        // (например, вызовом ensure() после ручного повтора): результат устаревшей
        // попытки не должен затирать более свежий.
        if (inFlight === running) {
          inFlight = null;
          publish({ status: 'ready', user });
        }
        return user;
      },
      (error: unknown) => {
        if (inFlight === running) {
          inFlight = null;
          publish(
            isMissingSignature(error)
              ? { status: 'unsigned', error }
              : { status: 'failed', error },
          );
        }
        // Отказ пробрасывается, а не гасится: вызвавший его защищённый запрос обязан
        // упасть с ЭТОЙ причиной, а не уйти на сервер без аккаунта и получить 404.
        throw error;
      },
    );

    inFlight = running;
    return running;
  }

  return {
    getState: () => state,

    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },

    ensure() {
      // Готовую сессию не перезапрашиваем: аккаунт уже есть, а `POST /session` на каждое
      // защищённое действие превратил бы одну запись в два запроса.
      if (state.status === 'ready') return Promise.resolve(state.user);
      if (inFlight) return inFlight;
      return attempt();
    },
  };
}
